import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import {
  useBarcodeScan,
  isWedgeScannerEnabled,
  WEDGE_SCANNER_STORAGE_KEY,
} from '../../hooks/useBarcodeScan';
import { IS_TOUCH_DEVICE } from '../../utils/touchDevice';
import BarcodeScannerModal from '../../components/ui/BarcodeScannerModal';
import { storageApi, StorageLocation } from '../../api/storage.api';
import { posApi } from '../../api/pos.api';
import { PackagePlus, ScanLine, MapPin, Check, RotateCcw, ArrowLeft, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';

interface ScannedLot {
  id: string;
  lot_number: string;
  product_name: string;
  stock_backroom: number;
  unassigned: number;
  expiry_date: string | null;
  unit: string | null;
}

interface ScannedLocation {
  id: string;
  label: string;
  qr_code: string;
}

type PutawayStep = 'scan_product' | 'scan_location' | 'confirm';

/** Un produit de la réserve et ses lots, tels que renvoyés par getBackroomLots. */
interface BackroomGroup {
  product?: { id?: string; name?: string; barcode?: string; code?: string };
  lots: any[];
}

export default function PutawayPage() {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [searchParams] = useSearchParams();
  const prefilledLotId = searchParams.get('lotId');

  const [step, setStep] = useState<PutawayStep>('scan_product');
  const [lot, setLot] = useState<ScannedLot | null>(null);
  const [location, setLocation] = useState<ScannedLocation | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  /** Emplacements actifs : permettent de choisir par libellé, sans douchette. */
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [wedgeOnTouch, setWedgeOnTouch] = useState(isWedgeScannerEnabled);
  /**
   * Désambiguïsation. Un même produit a souvent plusieurs lots en réserve, et un
   * mot cherché ramène souvent plusieurs produits. Prendre le premier venu, c'est
   * ranger une DLC sous une autre : l'emplacement dirait alors le contraire de ce
   * qu'il contient — exactement ce que le module est censé garantir.
   */
  const [productChoices, setProductChoices] = useState<BackroomGroup[]>([]);
  const [lotChoices, setLotChoices] = useState<{ productName: string; lots: any[] } | null>(null);

  // Charger un lot pré-rempli via URL
  useEffect(() => {
    if (prefilledLotId && tenant?.id) {
      loadLotById(prefilledLotId);
    }
  }, [prefilledLotId, tenant?.id]);

  // Liste des emplacements : sert au choix par libellé et à la liste déroulante.
  useEffect(() => {
    if (!tenant?.id) return;
    storageApi.getLocations({ active: true })
      .then(setLocations)
      .catch(() => setLocations([]));
  }, [tenant?.id]);

  const toggleWedge = () => {
    const next = !wedgeOnTouch;
    try {
      if (next) localStorage.setItem(WEDGE_SCANNER_STORAGE_KEY, '1');
      else localStorage.removeItem(WEDGE_SCANNER_STORAGE_KEY);
    } catch { /* mode privé : effet immédiat, non persistant */ }
    setWedgeOnTouch(next);
    toast.success(next ? 'Douchette activée sur cet appareil' : 'Douchette désactivée');
  };

  /**
   * Pose le lot choisi et le vrai maximum rangeable. `getBackroomLots` renvoie
   * des groupes { product, total_backroom, lots[] } : le nom et le code-barres
   * sont sous `.product`, pas à la racine — s'y tromper faisait echouer toute
   * recherche, y compris pour un article bien present.
   */
  const selectLot = async (found: any, productName: string) => {
    setProductChoices([]);
    setLotChoices(null);
    let unassigned = found.stock_backroom;
    try {
      const res = await storageApi.getUnassignedStock(found.id);
      unassigned = res.unassigned;
    } catch {
      // Best-effort : à défaut, on retombe sur le stock en réserve.
    }
    setLot({
      id: found.id,
      lot_number: found.lot_number,
      product_name: productName,
      stock_backroom: found.stock_backroom,
      unassigned,
      expiry_date: found.expiry_date,
      unit: found.unit,
    });
    setQuantity(unassigned > 0 ? unassigned : 0);
    setStep(location ? 'confirm' : 'scan_location');
    if (unassigned <= 0) {
      toast.warning(`${productName} : tout le stock de ce lot est déjà rangé`);
    } else {
      toast.success(`Produit : ${productName}`);
    }
  };

  /**
   * Un produit retenu : s'il a plusieurs lots en réserve, c'est à l'opérateur de
   * dire lequel il tient — lui seul voit la date sur le carton.
   */
  const pickProduct = async (group: BackroomGroup) => {
    setProductChoices([]);
    const productName = group.product?.name || 'Produit';
    const lots = group.lots || [];
    if (lots.length === 0) {
      toast.error(`${productName} : plus rien en réserve`);
      return;
    }
    if (lots.length === 1) {
      setLotChoices(null);
      await selectLot(lots[0], productName);
      return;
    }
    setLotChoices({ productName, lots });
  };

  const loadLotById = async (lotId: string) => {
    try {
      const groups = await posApi.getBackroomLots();
      for (const group of groups) {
        const found = group.lots.find((l: any) => l.id === lotId);
        if (found) {
          await selectLot(found, group.product?.name || 'Produit');
          return;
        }
      }
      toast.error('Lot non trouvé');
    } catch (err) {
      toast.error('Erreur lors du chargement du lot');
    }
  };

  const handleScan = useCallback(async (scanned: string) => {
    const value = scanned.trim();
    if (!value) return;

    // 1. QR d'emplacement
    if (value.startsWith('LOC:')) {
      try {
        const loc = await storageApi.getLocationByQR(value);
        setLocation({ id: loc.id, label: loc.label, qr_code: loc.qr_code });
        setStep(lot ? 'confirm' : 'scan_product');
        toast.success(`Emplacement : ${loc.label}`);
      } catch {
        toast.error('Emplacement non reconnu');
      }
      return;
    }

    // 2. Libellé d'emplacement tapé (A01.C03.E02). Le QR porte un identifiant
    //    aléatoire, intapable : sans cette voie, désigner un emplacement exige
    //    une douchette — inerte par défaut sur tablette.
    if (/^A\d+\.C\d+\.E\d+$/i.test(value)) {
      const label = value.toUpperCase();
      const match = locations.find((l) => l.label.toUpperCase() === label);
      if (match) {
        setLocation({ id: match.id, label: match.label, qr_code: match.qr_code });
        setStep(lot ? 'confirm' : 'scan_product');
        toast.success(`Emplacement : ${match.label}`);
      } else {
        toast.error(`Emplacement ${label} inconnu`);
      }
      return;
    }

    // 3. Code-barres, code produit, ou nom : chercher le lot en réserve.
    //    getBackroomLots renvoie { product, total_backroom, lots[] } — lire à la
    //    racine (product.barcode) faisait échouer TOUTE recherche.
    try {
      const groups: BackroomGroup[] = await posApi.getBackroomLots();
      const term = value.toLowerCase();

      // Un code-barres désigne un produit et un seul : on ne propose rien.
      // Un mot, lui, peut en désigner dix — d'où la liste.
      const exact = groups.filter((g) => g.product?.barcode === value || g.product?.code === value);
      const matches = exact.length > 0
        ? exact
        : term.length >= 3
          ? groups.filter((g) => g.product?.name?.toLowerCase().includes(term))
          : [];

      if (matches.length === 0) {
        toast.error('Produit non trouvé en réserve');
        return;
      }
      if (matches.length > 1) {
        setProductChoices(matches);
        setLotChoices(null);
        return;
      }
      await pickProduct(matches[0]);
    } catch {
      toast.error('Erreur lors de la recherche du produit');
    }
  }, [lot, location, locations]);

  useBarcodeScan({ onScan: handleScan, enabled: true, wedgeOnTouch });

  const handleConfirm = async () => {
    if (!lot || !location || quantity <= 0) return;

    setSaving(true);
    try {
      await storageApi.putaway({
        lotId: lot.id,
        locationQR: location.qr_code,
        quantity,
      });
      toast.success(`${lot.product_name} rangé en ${location.label}`);
      setLastResult(`${lot.product_name} × ${quantity} ${lot.unit || ''} → ${location.label}`);

      // Reset pour le prochain
      setLot(null);
      setLocation(null);
      setQuantity(0);
      setStep('scan_product');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors du rangement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLot(null);
    setLocation(null);
    setQuantity(0);
    setStep('scan_product');
    setLastResult(null);
    setProductChoices([]);
    setLotChoices(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header standalone */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[48px]"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <PackagePlus className="w-6 h-6 text-purple-600" />
          <h1 className="text-lg font-bold text-gray-900">Rangement</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/storage/locations')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors min-h-[48px]"
          >
            <Grid3X3 className="w-4 h-4" />
            Emplacements
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
      <div className="p-6 max-w-2xl mx-auto">

      {/* Dernier résultat */}
      {lastResult && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600 shrink-0" />
          <span className="text-sm text-green-800">{lastResult}</span>
        </div>
      )}

      {/* Steps indicators */}
      <div className="flex items-center gap-2 mb-6">
        <StepBadge
          number={1}
          label="Article"
          active={step === 'scan_product'}
          done={!!lot}
        />
        <div className="flex-1 h-px bg-gray-200" />
        <StepBadge
          number={2}
          label="Emplacement"
          active={step === 'scan_location'}
          done={!!location}
        />
        <div className="flex-1 h-px bg-gray-200" />
        <StepBadge
          number={3}
          label="Confirmer"
          active={step === 'confirm'}
          done={false}
        />
      </div>

      {/* Choix du produit quand la recherche en ramène plusieurs */}
      {step === 'scan_product' && productChoices.length > 0 && (
        <div className="mb-4 bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-blue-50 border-b text-sm font-medium text-blue-800">
            {productChoices.length} produits correspondent — lequel rangez-vous ?
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {productChoices.map((g, i) => (
              <button
                key={g.product?.id || i}
                onClick={() => pickProduct(g)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 min-h-[52px]"
              >
                <p className="font-medium">{g.product?.name || 'Produit'}</p>
                <p className="text-xs text-gray-500">
                  {g.lots?.length || 0} lot{(g.lots?.length || 0) > 1 ? 's' : ''} en réserve
                </p>
              </button>
            ))}
          </div>
          <button
            onClick={() => setProductChoices([])}
            className="w-full py-2 text-sm text-gray-500 hover:bg-gray-50 border-t"
          >
            Annuler
          </button>
        </div>
      )}

      {/* Choix du lot : la DLC est sur le carton, pas dans l'écran */}
      {step === 'scan_product' && lotChoices && (
        <div className="mb-4 bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-amber-50 border-b text-sm font-medium text-amber-800">
            {lotChoices.productName} — {lotChoices.lots.length} lots en réserve. Lequel avez-vous en main ?
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {lotChoices.lots.map((l: any) => (
              <button
                key={l.id}
                onClick={() => selectLot(l, lotChoices.productName)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 min-h-[52px] flex items-center justify-between gap-3"
              >
                <div>
                  <p className="font-mono text-sm font-medium">{l.lot_number}</p>
                  <p className="text-xs text-gray-500">
                    {l.expiry_date
                      ? `DLC ${new Date(l.expiry_date).toLocaleDateString('fr-BE')}`
                      : 'Sans DLC'}
                  </p>
                </div>
                <span className="text-sm font-medium shrink-0">
                  {l.stock_backroom} {l.unit || ''}
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={() => setLotChoices(null)}
            className="w-full py-2 text-sm text-gray-500 hover:bg-gray-50 border-t"
          >
            Annuler
          </button>
        </div>
      )}

      {/* Étape 1 : Scan article */}
      {step === 'scan_product' && productChoices.length === 0 && !lotChoices && (
        <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <ScanLine className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-lg font-medium text-gray-600">Scannez le code-barres de l'article</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">Ou saisissez le code manuellement</p>
          <form onSubmit={(e) => {
            e.preventDefault();
            const input = (e.target as HTMLFormElement).elements.namedItem('manualBarcode') as HTMLInputElement;
            if (input.value.trim()) {
              handleScan(input.value.trim());
              input.value = '';
            }
          }} className="flex items-center gap-2 max-w-md mx-auto px-4">
            <input
              name="manualBarcode"
              type="text"
              placeholder="Code-barres ou nom du produit..."
              autoFocus
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-center text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="submit"
              className="px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 active:scale-95"
            >
              OK
            </button>
          </form>
          <div className="mt-3 max-w-md mx-auto px-4 space-y-2">
            <button
              onClick={() => setScannerOpen(true)}
              className="w-full py-3 bg-gray-900 text-white font-medium rounded-lg active:scale-95"
            >
              📷 Scanner avec la caméra
            </button>
            {IS_TOUCH_DEVICE && (
              <button
                onClick={toggleWedge}
                className={`w-full min-h-[48px] px-3 rounded-lg border text-sm font-medium active:scale-95
                  ${wedgeOnTouch
                    ? 'bg-purple-50 border-purple-400 text-purple-700'
                    : 'bg-white border-gray-300 text-gray-600'}`}
              >
                {wedgeOnTouch
                  ? '✓ Douchette branchée — scan direct actif'
                  : '⌨️ J’ai branché une douchette sur cet appareil'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Étape 2 : Scan emplacement */}
      {step === 'scan_location' && lot && (
        <div>
          {/* Info article scanné */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Article sélectionné</p>
            <p className="text-lg font-bold">{lot.product_name}</p>
            <p className="text-sm text-gray-600">
              Lot {lot.lot_number} — Stock réserve : {lot.stock_backroom} {lot.unit || ''}
              {lot.expiry_date && ` — DLC : ${new Date(lot.expiry_date).toLocaleDateString('fr-BE')}`}
            </p>
          </div>

          <div className="py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 px-4">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-600 text-center">
              Scannez le QR de l'emplacement
            </p>
            {/* Le QR porte un identifiant aléatoire : sans ces deux voies,
                désigner un emplacement exigeait une douchette — et la page
                était inutilisable sur tablette. */}
            <div className="mt-4 max-w-md mx-auto space-y-2">
              <button
                onClick={() => setScannerOpen(true)}
                className="w-full py-3 bg-gray-900 text-white font-medium rounded-lg active:scale-95"
              >
                📷 Scanner avec la caméra
              </button>
              <label className="block text-sm font-medium text-gray-600 pt-2">
                ou choisissez l'emplacement
              </label>
              <select
                value={location?.id || ''}
                onChange={(e) => {
                  const match = locations.find((l) => l.id === e.target.value);
                  if (match) {
                    setLocation({ id: match.id, label: match.label, qr_code: match.qr_code });
                    setStep('confirm');
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg bg-white"
              >
                <option value="">— Emplacement —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {[l.label, l.aisle_name, l.name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select>
              {locations.length === 0 && (
                <p className="text-sm text-amber-700">
                  Aucun emplacement créé. Passez par « Emplacements » pour les définir.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Étape 3 : Confirmation */}
      {step === 'confirm' && lot && location && (
        <div>
          {/* Résumé */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Article</p>
            <p className="text-lg font-bold">{lot.product_name}</p>
            <p className="text-sm text-gray-600">Lot {lot.lot_number}</p>
          </div>

          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-purple-600 font-medium">Emplacement</p>
            <p className="text-2xl font-bold font-mono">{location.label}</p>
          </div>

          {/* Quantité */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantité ({lot.unit || 'unités'})
            </label>
            <input
              type="number"
              step="0.001"
              min="0.001"
              max={lot.unassigned}
              value={quantity}
              onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
              className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              Max : {lot.unassigned} {lot.unit || ''} (stock non rangé)
            </p>
          </div>

          {/* Boutons */}
          <div className="flex gap-3">
            <button
              onClick={handleReset}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Recommencer
            </button>
            <button
              onClick={handleConfirm}
              disabled={saving || quantity <= 0}
              className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px]"
            >
              {saving ? (
                <span className="animate-spin">...</span>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Confirmer le rangement
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
      </div>

      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={(code) => { setScannerOpen(false); void handleScan(code); }}
        title={step === 'scan_location' ? "Scanner l'emplacement" : "Scanner l'article"}
      />
    </div>
  );
}

function StepBadge({ number, label, active, done }: { number: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
      done ? 'bg-green-100 text-green-700' :
      active ? 'bg-blue-100 text-blue-700' :
      'bg-gray-100 text-gray-400'
    }`}>
      {done ? <Check className="w-4 h-4" /> : <span>{number}</span>}
      <span>{label}</span>
    </div>
  );
}
